import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useCommonCarTypeCatalog, useUpsertDispatchCarSpec } from "../hooks/useCarSpecs";
import type { DispatchCarSpecItem } from "../types/carSpec.types";

interface CarSpecDialogProps {
  open: boolean;
  companyGuid: string;
  /** 수정 대상. null이면 신규 등록. */
  spec: DispatchCarSpecItem | null;
  onClose: () => void;
}

export function CarSpecDialog({ open, companyGuid, spec, onClose }: CarSpecDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{spec ? "전용 톤수 수정" : "전용 톤수 추가"}</DialogTitle>
          <DialogDescription>
            이 업체에만 보이는 톤수입니다. 정보망에 없는 톤수라 정보망 접수는 불가하고 직접 배차만
            가능합니다.
          </DialogDescription>
        </DialogHeader>

        {/* 폼 상태는 마운트 시 spec에서 초기화한다 — 대상이 바뀌면 key로 새로 만든다. */}
        <CarSpecForm
          key={spec?.guid ?? "new"}
          companyGuid={companyGuid}
          spec={spec}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

interface CarSpecFormProps {
  companyGuid: string;
  spec: DispatchCarSpecItem | null;
  onClose: () => void;
}

function CarSpecForm({ companyGuid, spec, onClose }: CarSpecFormProps) {
  const { data: catalog = [] } = useCommonCarTypeCatalog();
  const upsert = useUpsertDispatchCarSpec(companyGuid);

  const [carWeightStr, setCarWeightStr] = useState(spec?.carWeightStr ?? "");
  const [carWeightValue, setCarWeightValue] = useState(spec ? String(spec.carWeightValue) : "");
  // 차종 → 선택된 차체옵션. 차종 선택 자체가 이 map의 키 존재 여부다.
  const [optionsByType, setOptionsByType] = useState<Record<string, string[]>>(() =>
    Object.fromEntries((spec?.carTypes ?? []).map((t) => [t.name, t.carBodyOptions])),
  );

  const isEditMode = !!spec;
  const selectedTypes = Object.keys(optionsByType);

  const toggleType = (name: string) => {
    setOptionsByType((prev) => {
      if (name in prev) {
        const { [name]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [name]: [] };
    });
  };

  const toggleOption = (typeName: string, option: string) => {
    setOptionsByType((prev) => {
      const current = prev[typeName] ?? [];
      return {
        ...prev,
        [typeName]: current.includes(option)
          ? current.filter((o) => o !== option)
          : [...current, option],
      };
    });
  };

  const handleSave = () => {
    upsert.mutate(
      {
        DispatchCompanyGuid: companyGuid,
        CarWeightStr: carWeightStr.trim(),
        CarWeightValue: Number(carWeightValue),
        CarTypes: selectedTypes.map((name) => ({
          Name: name,
          CarBodyOptions: optionsByType[name] ?? [],
        })),
      },
      { onSuccess: onClose },
    );
  };

  // 서버가 최종 검증하지만(중복 톤수·'미만' 포함·미등록 차종/옵션), 빈 값으로 400을 받는 건 막는다.
  const canSave =
    carWeightStr.trim().length > 0 &&
    Number(carWeightValue) > 0 &&
    selectedTypes.length > 0 &&
    !upsert.isPending;

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">톤수 표기</label>
          <Input
            value={carWeightStr}
            onChange={(e) => setCarWeightStr(e.target.value)}
            placeholder="예: 1.5톤"
            disabled={isEditMode}
            className="h-7 text-xs"
          />
          <span className="text-xs text-gray-400">
            {isEditMode
              ? "톤수 표기는 화물·단가에 저장된 값이라 수정할 수 없습니다. 바꾸려면 삭제 후 새로 추가하세요."
              : "화면에 그대로 표시됩니다. '미만'은 경차 처리 규칙과 충돌해 사용할 수 없습니다."}
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">톤수 값</label>
          <Input
            type="number"
            step="0.1"
            value={carWeightValue}
            onChange={(e) => setCarWeightValue(e.target.value)}
            placeholder="예: 1.5"
            className="h-7 text-xs"
          />
          <span className="text-xs text-gray-400">
            정렬 순서와 화물 중량 자동계산에 쓰입니다.
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">차종 / 추가옵션</label>
          <div className="flex max-h-52 flex-col gap-1 overflow-auto rounded border border-gray-200 p-2">
            {catalog.length === 0 ? (
              <span className="text-xs text-gray-400">차종 목록을 불러오는 중...</span>
            ) : (
              catalog.map((type) => {
                const isSelected = type.name in optionsByType;
                return (
                  <div key={type.name} className="flex flex-col">
                    <label className="flex cursor-pointer items-center gap-1 text-xs">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleType(type.name)}
                      />
                      {type.name}
                    </label>

                    {/* 옵션은 차종을 골라야 의미가 있다 — 접어두면 어떤 옵션이 붙었는지 안 보인다 */}
                    {isSelected && type.carBodyOptions.length > 0 && (
                      <div className="my-0.5 ml-5 flex flex-wrap gap-x-3 gap-y-1 border-l border-gray-200 pl-2">
                        {type.carBodyOptions.map((opt) => (
                          <label
                            key={opt}
                            className="flex cursor-pointer items-center gap-1 text-xs text-gray-600"
                          >
                            <Checkbox
                              checked={(optionsByType[type.name] ?? []).includes(opt)}
                              onCheckedChange={() => toggleOption(type.name, opt)}
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <span className="text-xs text-gray-400">
            표준 차종·옵션만 고를 수 있습니다. 새로 만들 수는 없습니다. 옵션을 고르지 않으면 그
            차종은 옵션 없이 등록됩니다.
          </span>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose} className="h-7 text-xs">
          취소
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
          className="h-7 bg-blue-600 text-xs font-bold hover:bg-blue-700"
        >
          저장
        </Button>
      </div>
    </>
  );
}
